/**
 * Converts database chat messages to UIMessage format for useChat.setMessages().
 *
 * Only includes user and assistant messages (system/tool messages are internal).
 *
 * HUB-060 redesign: for assistant messages with non-null toolResults, this now
 * materialises a `tool-<toolName>` part per tool call WITH the preserved HTTP
 * metadata (outputStatus/outputCode/outputMessage), and — for non-200 responses
 * — ALSO emits a sibling `data-tf-tool-output-http-metadata` part on the same
 * message so the dispatcher's join logic (`collectCompletedToolResults`) works
 * identically on replay vs live stream.
 *
 * Back-compat: old rows stored raw JSON strings in toolResults[], not objects.
 * Those rows are silently skipped (no tool parts emitted), which preserves
 * prior behaviour for sessions that predate HUB-060.
 */
export function convertToUIMessages(dbMessages) {
    return dbMessages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => {
        // Prefer the server-hydrated faithful transcript when present: it carries
        // the exact ordered parts (text/tool/cards) as rendered, so reload is 1:1.
        if (Array.isArray(m.uiParts) && m.uiParts.length > 0) {
            return {
                id: m.id,
                role: m.role,
                content: m.content ?? '',
                parts: m.uiParts,
            };
        }
        const textPart = m.content ? [{ type: 'text', text: m.content }] : [];
        // HUB-060: materialise tool-result parts for assistant messages.
        const toolParts = [];
        const metadataParts = [];
        if (m.role === 'assistant' && Array.isArray(m.toolResults) && m.toolResults.length > 0) {
            for (const raw of m.toolResults) {
                // Back-compat: old rows stored JSON strings, not objects.
                // Skip those silently.
                if (!raw || typeof raw !== 'object' || Array.isArray(raw))
                    continue;
                const part = raw;
                // Must have at least toolName and state to be a valid ToolResultMessagePart.
                if (typeof part.toolName !== 'string' || !part.toolName)
                    continue;
                if (typeof part.state !== 'string' || !part.state)
                    continue;
                const toolCallId = typeof part.toolCallId === 'string' ? part.toolCallId : null;
                const state = part.state; // 'output-available' or 'output-error'
                // Build the tool-<toolName> part matching the live-stream shape.
                const toolPart = {
                    type: typeof part.type === 'string' ? part.type : `tool-${part.toolName}`,
                    state,
                    toolName: part.toolName,
                    toolCallId,
                    output: part.output,
                };
                // Preserve HTTP metadata fields when present (non-200 responses).
                if (typeof part.outputStatus === 'number') {
                    toolPart.outputStatus = part.outputStatus;
                }
                if (part.outputCode !== undefined) {
                    toolPart.outputCode = part.outputCode;
                }
                if (part.outputMessage !== undefined) {
                    toolPart.outputMessage = part.outputMessage;
                }
                toolParts.push(toolPart);
                // For non-200 responses, emit a sibling data-tf-tool-output-http-metadata
                // part so the dispatcher's join (collectCompletedToolResults) works on
                // replay exactly as it does on a live stream.
                const outputStatus = typeof part.outputStatus === 'number' ? part.outputStatus : 200;
                if (outputStatus !== 200) {
                    metadataParts.push({
                        type: 'data-tf-tool-output-http-metadata',
                        data: {
                            toolCallId,
                            status: outputStatus,
                            code: typeof part.outputCode === 'string' ? part.outputCode : null,
                            message: typeof part.outputMessage === 'string' ? part.outputMessage : null,
                        },
                    });
                }
            }
        }
        // Interleave: metadata part immediately before its sibling tool part.
        // In practice the AI SDK reducer is position-agnostic (the dispatcher
        // builds an index by toolCallId), but keeping them adjacent is tidier.
        const allParts = [];
        for (let i = 0; i < toolParts.length; i++) {
            const toolPart = toolParts[i];
            const matchingMeta = metadataParts.find((mp) => mp.data?.toolCallId === toolPart.toolCallId);
            if (matchingMeta)
                allParts.push(matchingMeta);
            allParts.push(toolPart);
        }
        return {
            id: m.id,
            role: m.role,
            content: m.content ?? '',
            parts: [
                ...textPart,
                ...allParts,
            ],
        };
    });
}
//# sourceMappingURL=convert-messages.js.map